import { Field, Form, Formik, FormikProps } from 'formik';
import { useEffect, useState } from 'react';
import InvoicingLogo from '../../../assets/Invoicing.svg';
import Input from '../../../components/Input/Input';
import Select, { SelectProps } from '../../../components/Input/Select';
import { getBusinessCategories, getBusinessTypes, getTaxPayers } from '../../../services/commerce.service';
import {
  AddressTaxesValues,
  BusinessCategory,
  CommerceAddressState,
  TaxPayer,
} from '../../../types/add_commerce.types';
import { BusinessType, LawFormField, lawsFormFields } from '../../../utils/data';
import { sortArrayAlphabetically } from '../../../utils/helpers';
import schemas from '../../../utils/schemas';
import { AddressCitySelect, AddressStateSelect } from './AddressSelect';
import DgiSection from './DgiSection';

function AddressForm({
  formRef,
  localState,
  handleSubmit,
}: {
  formRef: React.MutableRefObject<FormikProps<any> | null>;
  localState: CommerceAddressState;
  handleSubmit: (values: AddressTaxesValues) => void;
}) {
  const [businessTypes, setBusinessTypes] = useState<Array<BusinessType>>([]);
  const [businessCategories, setBusinessCategories] = useState<Array<BusinessCategory>>([]);
  const [taxPayers, setTaxPayers] = useState<Array<TaxPayer>>([]);

  const fetchBusinessCategories = async () => {
    const responseBusinessCategories = await getBusinessCategories();
    if (Array.isArray(responseBusinessCategories.data)) {
      const sortedResponse = sortArrayAlphabetically(responseBusinessCategories.data, 'name');
      setBusinessCategories(sortedResponse);
    }
  };

  const fetchTaxPayers = async () => {
    const responseTaxPayers = await getTaxPayers();
    if (Array.isArray(responseTaxPayers.data)) {
      const sortedResponse = sortArrayAlphabetically(responseTaxPayers.data, 'description');
      setTaxPayers(sortedResponse);
    }
  };

  const fetchBusinessTypes = async (businessCategoryId: number) => {
    const serviceResponse = await getBusinessTypes(businessCategoryId);

    setBusinessTypes(serviceResponse.data);
  };

  useEffect(() => {
    fetchBusinessCategories();
    fetchTaxPayers();
  }, []);

  useEffect(() => {
    if (!localState.businessCategoryId || businessTypes.length > 0) return;
    fetchBusinessTypes(+localState.businessCategoryId);
  }, [localState.businessCategoryId, businessTypes.length]);

  const categoryChangeHandler: SelectProps['handleOnChange'] = async (targetValue, setFieldValue) => {
    if (targetValue) {
      await fetchBusinessTypes(+targetValue);
    } else {
      setBusinessTypes([]);
    }

    setFieldValue('businessCategoryId', +targetValue);
  };

  return (
    <Formik
      innerRef={(formElement) => (formRef.current = formElement)}
      initialValues={{
        stateId: localState?.city?.stateId.toString() || '',
        cityId: localState?.city?.id.toString() || '',
        addressStreet: localState?.addressStreet || '',
        addressNumber: localState?.addressNumber || '',
        addressFlatNumber: localState?.addressFlatNumber || '',
        addressSolar: localState?.addressSolar || '',
        addressPlot: localState?.addressPlot || '',
        businessCategoryId: localState?.businessCategoryId || '',
        taxpayerTypeId: localState?.taxpayerTypeId || '',
        law_19210: localState?.law_19210 || false,
        law_17934: localState?.law_17934 || false,
        law_18083: localState?.law_18083 || false,
        law_18910: localState?.law_18910 || false,
        businessType: localState?.businessType || 0,
      }}
      validationSchema={schemas.AddressTaxesSchema}
      onSubmit={handleSubmit}
    >
      <Form className="row">
        <AddressStateSelect />
        <AddressCitySelect localState={localState} />
        <Input className="input col-12" type="text" name="addressStreet" label="Calle" required />
        <Input className="input col-md-3" type="text" name="addressNumber" label="N° de puerta" required />
        <Input className="input col-md-3" type="number" name="addressFlatNumber" label="Apto (opcional)" />
        <Input className="input col-md-3" type="number" name="addressSolar" label="Solar (opcional)" />
        <Input className="input col-md-3" type="number" name="addressPlot" label="Manzana (opcional)" />

        <DgiSection
          localState={localState}
          businessCategories={businessCategories}
          taxPayers={taxPayers}
          fetchBusinessTypes={fetchBusinessTypes}
        />
        <Select
          className="col-12"
          name="businessCategoryId"
          handleOnChange={categoryChangeHandler}
          label="Rubro"
          required
        >
          {businessCategories?.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </Select>
        <Select className="col-12" name="businessType" label="Tipo empresa" required>
          {businessTypes?.map((businessType) => (
            <option key={businessType.value} value={businessType.value}>
              {businessType.name}
            </option>
          ))}
        </Select>

        <h1 className="form-title my-3">
          <img src={InvoicingLogo} alt="Comercio" className="me-3" /> Información tributaria
        </h1>
        <Select className="col-12" name="taxpayerTypeId" label="Tipo de contribuyente" required>
          {taxPayers?.map((taxPayer) => (
            <option key={taxPayer.id} value={taxPayer.id}>
              {taxPayer.description}
            </option>
          ))}
        </Select>
        <p className="input-title">(Seleccionar en caso de que corresponda)</p>
        {lawsFormFields.map((law: LawFormField, index: number) => (
          <label className="col checkbox-label" key={index}>
            <Field className="checkbox" type="checkbox" name={law.fieldname} />
            <span>{law.label}</span>
          </label>
        ))}
      </Form>
    </Formik>
  );
}

export default AddressForm;
